import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { TrustJetLogoWhite, TrustJetPlane } from '@assets/images';
import { Logo } from '@components/branding/logo';
import { useAuthStore } from '@store/authStore';
import { ROUTES } from '@/constants';
import { cn } from '@components/ui/utils';

const AuthLayout = ({
  children,
  title,
  subtitle,
  showLogo = true,
  maxWidth = "md", // sm, md, lg, xl
  className = ""
}) => {
  const { setAuthType, authType } = useAuthStore();
  const location = useLocation();
  const [isHydrated, setIsHydrated] = useState(false);

  // Wait for Zustand hydration
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Set browser title based on the page
  useEffect(() => {
    let pageTitle = "TrustJet";

    switch(authType) {
      case "signin":
        pageTitle = "Sign In - TrustJet";
        break;
      case "signup":
        pageTitle = "Sign Up - TrustJet";
        break;
      case "mfa":
        pageTitle = "MFA Verification - TrustJet";
        break;
      case "home":
      default:
        pageTitle = "Welcome to TrustJet - Private skies, verified trust";
        break;
    }

    document.title = pageTitle;
  }, [authType]);

  // Show loading state during hydration to prevent flash
  if (!isHydrated) {
    return (
      <div
        className="min-h-screen w-full flex items-center justify-center"
        style={{ backgroundColor: '#1e40af' }}
      >
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  const isHome = authType === "home" && location.pathname === ROUTES.HOME.path;

  // Simplified full-screen layout for homepage
  if (isHome) {
    const handleSignup = () => {
      console.log("Signup clicked");
      setAuthType("signup");
    };

    const handleSignin = () => {
      console.log("Signin clicked");
      setAuthType("signin");
    };

    return (
      <div
        className="min-h-screen w-full bg-cover bg-no-repeat flex flex-col overflow-x-hidden relative"
        style={{
          backgroundImage: `url('${TrustJetPlane}')`,
          backgroundColor: '#1e40af',
          backgroundPosition: 'center 40%',
        }}
      >
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 flex-1 flex flex-col">
        {/* Content - centered vertically on mobile, positioned from bottom on larger screens */}
        <div className="flex-1 flex flex-col justify-end pb-16 md:pb-[15rem] px-6">
          <div className="text-white text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <Logo className="h-auto" logoImage={TrustJetLogoWhite} />
            </div>
            <div className="max-w-[500px] m-auto flex flex-col gap-4 mt-5 px-4 md:px-0">
              <button
                type="button"
                className="w-full bg-primary-500 hover:bg-primary-600 active:bg-primary-700 text-white rounded-full font-normal py-4 cursor-pointer text-base select-none"
                onClick={handleSignup}
              >
                Create an Account
              </button>
              <button
                type="button"
                className="w-full bg-white text-gray-500 hover:bg-primary-50 active:bg-primary-100 rounded-full font-normal py-4 cursor-pointer text-base select-none"
                onClick={handleSignin}
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // Original layout for signin/signup forms
  return (
    <div className="min-h-screen w-full flex items-start lg:items-center justify-center relative bg-gray-50 p-0 lg:p-0 overflow-x-hidden">
      <div className={cn(
          `relative w-full transition-all duration-500 ease-in-out max-w-[900px]`,
          className
        )}>

            <div className="bg-white lg:rounded-2xl shadow-xl">

            <div className="min-h-screen lg:min-h-0 lg:h-[85vh] flex flex-col lg:flex-row">

                {/* Left Side - Airplane Background */}
                <div className="hidden lg:flex lg:w-1/2 h-full relative">

                  <div
                    className="w-full h-full bg-cover bg-no-repeat relative rounded-l-2xl overflow-hidden"
                    style={{
                      backgroundImage: `url('${TrustJetPlane}')`,
                      backgroundColor: '#1e40af',
                      backgroundPosition: '40% 0%',
                      backgroundSize: 'cover'
                    }}
                  >
                    {/* Overlay for better text readability */}
                    <div className="absolute inset-0 bg-black/20"></div>

                    {/* TrustJet Branding */}
                    <div className="absolute bottom-8 text-white text-center w-full px-6">
                      <div className="flex items-center justify-center space-x-2 mb-2">
                        <Logo className="h-auto" logoImage={TrustJetLogoWhite} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Side - Form */}
                <div className="w-full lg:w-1/2 flex min-h-screen lg:min-h-0">
                  <div className="w-full p-6 lg:p-6 flex flex-col flex-1 overflow-y-auto">
                    {children}
                  </div>
                </div>

            </div>

          </div>

        </div>
    </div>
  );
};

export default AuthLayout;
